import { Sleep, genRanID } from "../utils/utils";
import { GLOBAL_VARS } from "../services/globalVars";
import { CAST_Update_Screen, CAST_Rest_Clock } from "./cast-control";
import { isCastApiAvailable, getCastState } from "./cast-utils";
import Swal from 'sweetalert2';

var applicationId = import.meta.env.VITE_CAST_RECEIVER_ID; // "E3D6BA50"; //61F63EDA
var namespace = "urn:x-cast:ScoringCasting"
var Context = null;
var castSession = null;
var message_buffer = [];
const castStateListeners = new Set();
let lastCastFailureAlertAt = 0;
let sessionRequestInFlight = false;

const CAST_ERROR_MESSAGES = {
   invalid_parameter: 'Could not open Cast. (This is the ISSUE we are fighting)',
   cancel: null,
   timeout: 'Cast timed out. Check Wi-Fi and try again.',
   receiver_unavailable: 'No compatible Cast device found on this network.',
   session_error: 'Cast session failed. Please try again.',
};

const repairCastSession = (errorCode) => {
   if (!errorCode || errorCode === 'cancel') {
      console.error('Returning early from notifyCastSessionFailed');
      return;
   }

   if (errorCode === 'invalid_parameter') {
      console.error('Chromecast has a known issue. Click FIX CAST and try again.');
      Swal.fire({
         icon: 'error',
         title: 'Chromecast connection failed',
         text: 'Chromecast has a known issue. Click FIX CAST and try again.',
         confirmButtonText: 'FIX CAST',
      }).then(() => {
         // Reload the current page, effectively resetting and refreshing the app
         window.location.reload();
      });
      return;
   } else {
      notifyCastSessionFailed(errorCode);
   }
}

const notifyCastSessionFailed = (errorCode) => {
   try {
      console.log('[CastDebug] notifyCastSessionFailed: ', errorCode);
      if (!errorCode || errorCode === 'cancel') {
         console.error('Returning early from notifyCastSessionFailed');
         return;
      }
   
      const now = Date.now();
      if (now - lastCastFailureAlertAt < 500) {
         console.log('Returning because of timeout');
         return;
      }
      lastCastFailureAlertAt = now;
      
      const message = CAST_ERROR_MESSAGES[errorCode] ?? `Cast failed (${errorCode}). Please try again.`;
   
      console.log('[CastDebug] Cast session failed alert:', errorCode, message);
   
      console.warn('Fire Swal');
      Swal.fire({
         icon: 'error',
         title: 'Cast connection failed',
         text: message,
         confirmButtonText: 'OK',
      });
      
   } catch (error) {
      console.error('Error in notifyCastSessionFailed: ', error);
   }
};

const notifyCastStateListeners = (castState) => {
   castStateListeners.forEach((listener) => listener(castState));
};

const subscribeCastStateChanged = (listener) => {
   castStateListeners.add(listener);
   return () => castStateListeners.delete(listener);
};

const logCastError = (context, error) => {
   const payload = {
      context,
      error,
      type: typeof error,
      code: error?.code,
      description: error?.description,
      message: error?.message,
      details: error?.details,
   };

   if (error && typeof error === 'object') {
      payload.keys = Object.keys(error);
   }

   try {
      payload.serialized = JSON.stringify(error);
   } catch {
      payload.serialized = String(error);
   }

   console.log('[CastDebug] Cast error', payload);
   console.log('[CastDebug] applicationId: ', applicationId);
};

// console.log('applicationId: ', applicationId);

/**
 * Call initialization for Cast
 */
window["__onGCastApiAvailable"] = function (loaded, errorInfo) {
   console.log('[CastDebug] __onGCastApiAvailable', { loaded, errorInfo });
   if (loaded) {
      console.debug('%c Casting Starting up', 'color: #00adff');
      init_cast();
   } else {
      console.log('[CastDebug] Cast sender script failed to load', errorInfo);
   }
}

const isFrameworkLoaded = () => {
   return isCastApiAvailable();
}
const wait_for_framework = () => {
   return new Promise(async (resolve) => {
      let result = await Sleep(100, isFrameworkLoaded, 10);
      resolve(result);
   })
}

// const onError = (e) => { console.error('Cast on Error: ', e) }
// const onSuccess = (e) => { console.error('Cast on Success: ', e) }

const resetCastSession = () => {
   castSession = null;
   GLOBAL_VARS.cast_running = false;
   CAST_Rest_Clock();
};

const getCastContext = () => {
   if (!isCastApiAvailable()) {
      return null;
   }
   return window.cast.framework.CastContext.getInstance();
};

const clearCastSession = () => {
   console.warn('clearCastSession');
   const ctx = Context || getCastContext();
   if (ctx?.endCurrentSession) {
      try {
         ctx.endCurrentSession(true);
      } catch (error) {
         console.debug('Cast endCurrentSession:', error);
      }
   }
   resetCastSession();
};

const refreshCastSession = () => {
   castSession = getCastContext()?.getCurrentSession() ?? null;
   return castSession;
};

const onCastConnected = () => {
   refreshCastSession();
   if (!castSession) {
      console.debug('Cast CONNECTED but no session available');
      return;
   }

   GLOBAL_VARS.cast_running = true;

   castSession.addMessageListener(namespace, (namespaceID, msg) => {
      message_buffer = msg;

      setTimeout(function () {
         // sendMessage({ data: 'Message Received' });
      }, 5000);
   });

   sendMessage({ data: 'Starting Connection' });
   setTimeout(function () {
      CAST_Update_Screen();
      keep_alive();
   }, 500);
};

const requestCastSession = () => {
   const ctx = Context || getCastContext();
   if (!ctx) {
      console.log('[CastDebug] requestCastSession skipped: framework not ready');
      return Promise.resolve(false);
   }

   if (sessionRequestInFlight) {
      console.log('[CastDebug] requestCastSession skipped: already in progress');
      return Promise.resolve(false);
   }

   sessionRequestInFlight = true;
   console.log('[CastDebug] requestCastSession: prompting device picker');
   return ctx.requestSession()
      .then(() => {
         console.log('[CastDebug] requestCastSession: session established');
         return true;
      })
      .catch((error) => {
         logCastError('requestCastSession', error);
         const errorCode = typeof error === 'string' ? error : error?.code;
         console.log('[CastDebug] [requestCastSession] catch: ', errorCode);
         // notifyCastSessionFailed(errorCode);
         repairCastSession(errorCode);
         clearCastSession();
         return false;
      })
      .finally(() => {
         sessionRequestInFlight = false;
      });
};

const init_cast = () => {
   console.debug('>>> init_cast <<<');
   console.log('[DEBUG] Cast Service: applicationId: ', applicationId);
   // console.debug('Cast: ', window.cast);
   // console.debug('Chrome cast: ', window.chrome.cast)

   wait_for_framework().then(async() => {
      await Sleep(1000);
      console.debug('Load Cast: ', window.cast);
      console.debug('Load Chrome cast: ', window.chrome.cast)
      window.cast.framework.setLoggerLevel(window.cast.framework.LoggerLevel.DEBUG);
      Context = window.cast.framework.CastContext.getInstance();
      Context.setOptions({
         receiverApplicationId: applicationId,
         autoJoinPolicy: window.chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED,
         resumeSavedSession: false
         // resumeSavedSession: true
      });

      Context.addEventListener(window.cast.framework.CastContextEventType.SESSION_STATE_CHANGED, (e) => {
         refreshCastSession();
         console.log('[CastDebug] SESSION_STATE_CHANGED', e.sessionState, e);

         if (e.sessionState === "SESSION_START_FAILED") {
            logCastError('SESSION_START_FAILED', e);
            console.log('[CastDebug] [init_cast] SESSION_START_FAILED: ', e?.errorCode);
            // notifyCastSessionFailed(e.errorCode);
            repairCastSession(e.errorCode);
            clearCastSession();
         } else if (e.sessionState === "SESSION_STARTED") {
            refreshCastSession();
         } else if (e.sessionState === "SESSION_RESUMED") {
            console.warn('Cast RESUMED');
            GLOBAL_VARS.cast_running = true;
            refreshCastSession();
         } else if (e.sessionState === "SESSION_ENDED") {
            resetCastSession();
         }
      });

      Context.addEventListener(window.cast.framework.CastContextEventType.CAST_STATE_CHANGED, (e) => {
         console.log('[CastDebug] CAST_STATE_CHANGED', e.castState, '(launcher icon visibility follows device discovery)');
         notifyCastStateListeners(e.castState);
         if (e.castState === 'CONNECTED') {
            onCastConnected();
         }
      });

      notifyCastStateListeners(Context.getCastState());
      console.debug('......CAST FRAMEWORK UP......');
   });
}

const keep_alive = async () => {
   try {
      const ok = await sendMessage({
         data: 'keep_alive',
         control: [{
            message_id: genRanID(),
            control: 'keep_alive',
            data: '',
         }]
      }, true);
      if (!ok || !GLOBAL_VARS.cast_running) {
         return;
      }
      await Sleep(30_000);
      keep_alive();
   } catch (error) {
     console.error('ERROR - CAST Service - keep_alive: ', error);
   }
}

// const auto_loop = (session) => {
//    let url = "https://ia802508.us.archive.org/5/items/testmp3testfile/mpthreetest.mp3"
//    var mediaInfo = new window.chrome.cast.media.MediaInfo(url, 'audio/aac');
//    mediaInfo.metadata = new window.chrome.cast.media.MusicTrackMediaMetadata()
//    mediaInfo.metadata.title = 'Hidden Tract';
//    // mediaInfo.metadata.subtitle = 'This is the name of the artist';
//    // mediaInfo.metadata.albumName = 'This is the name of the album'
//    // mediaInfo.metadata.artistName = 'This is the name of the artist'
//    // mediaInfo.metadata.songName = 'This is the name of the song'
//    // let im = window.chrome.cast.Image('http://m1.behance.net/rendition/modules/575407/disp/822271229466847.png')
//    // mediaInfo.metadata.images = new Array(im)
//    var request = new window.chrome.cast.media.LoadRequest(mediaInfo);
//    console.log('HI ', request);
//    console.log('castSession ', session);
//    // castSession.loadMedia(request,Context.onMediaDiscovered.bind(this, 'loadMedia'), Context.onMediaError())
//    session.loadMedia(request).then((e) => { 
//       console.log('After loadMedia', e) 
//       Sleep(12000).then(() => {
//          console.log('Running AGAIN -------------') ;
//          auto_loop(session);
//       })
//    });
// }

const await_message_reply = (control, id) => {
   return new Promise(async (resolve) => {
      // console.log('>>> await_message_reply <<<');
      // console.log('control: ', control);
      // console.log('id: ', id);

      const max = 100;
      for (let a = 0; a < max; a++) {
         // console.log('message_buffer: ', message_buffer);
         if (typeof message_buffer === 'string') {
            try {
               const msg = JSON.parse(message_buffer)
               // console.log('X: '+msg[0].control+' - '+control);
               // console.log('Y: '+msg[0].id+' - '+id);
               if (msg[0].control === control && msg[0].id === id) {
                  // console.warn('We have a hit - ', control);
                  resolve(msg[0].data);
                  return true;
               }
            } catch (error) {
            }
         }
         await Sleep(200);
      }
      resolve(false);
   });
}

/**
 * send a message to the receiver using the custom namespace
 * receiver CastMessageBus message handler will be invoked
 * @param {string} message A message string
 */
const sendMessage = (message, await_result = false) => {
   return new Promise((resolve) => {
      try {
         // console.debug('sendMessage: ', message);
         // var data = message;
         // console.log('castSession: ', castSession);
         if (castSession != null) {
            // console.debug('namespace: ', namespace)
            castSession.sendMessage(namespace, message).then(msg => {
               if (await_result) {
                  // console.log('sendMessage msg: ', message)
                  await_message_reply(message.control[0].control, message.control[0].message_id).then(result => {
                     console.debug('await_message_reply -- result: ', result);
                     resolve(result);
                  });
               } else {
                  resolve(true);
               }
            }).catch(err => {
               console.debug('sendMessage ERR: ', err)
               resolve(false);
            });
         } else {
            resolve(false);
         }
      } catch (error) {
         console.error('ERROR sendMessage');
         resolve(false);
      }
   });
}

function onLoad() { init_cast(); }
if (window.attachEvent) { window.attachEvent('onload', onLoad); }

export { sendMessage, requestCastSession, subscribeCastStateChanged, getCastState };